// Section 17 and 22 shortage dialog, the overlay root: the game's signature screen and the only
// place the turn pauses mid-resolution. Opens by itself when the season is paused on a shortage
// prompt (reads the `status` and `game` signals); confirming answers it with `answerShortage`,
// which resumes the season. The header, the live projection strip (recomputed through
// projectShortage with a 60 ms debounce, greyed while recomputing), every person as a row with
// full / half / none and a pin, a consequence hint per row, the seed corn control, and the four
// buttons. No timer. Esc never discards edits without a confirm. Other components take props.
import { useComputed } from '@preact/signals';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Config, GameState, PersonId } from '../../sim/api';
import { answerShortage, game, status } from '../../store';
import { jobLabel } from '../../viewModels/people';
import {
  equalShares,
  hasEdits,
  noEdits,
  shortageAnswer,
  shortageHints,
  shortageStrip,
  shortageVM,
  type ShortageAnswer,
  type ShortageEdits,
  type ShortageHint,
  type ShortageStrip,
  type ShortageVM,
} from '../../viewModels/shortage';
import { rationPolicyLabel, rationResourceLabel, seasonLabel, shortageText } from '../text/en';
import { ProjectionStrip } from './projection-strip';
import { ShortageRow } from './shortage-row';
import './shortage.css';

/** Section 24.8: recompute the strip 60 ms after the last edit. */
export const STRIP_DEBOUNCE_MS = 60;

type Idle = (cb: () => void) => void;
const whenIdle: Idle = (cb) => {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
    .requestIdleCallback;
  if (ric === undefined) cb();
  else ric(cb);
};

function hintText(hint: ShortageHint | undefined): string {
  if (hint === undefined) return shortageText.hintPending;
  if (hint.secondSeason) return shortageText.secondSeason(hint.healthNow, hint.healthIfNone);
  if (hint.strengthIfNone === null) return shortageText.hintPending;
  return shortageText.ifNone(
    hint.strengthNow,
    hint.strengthIfNone,
    jobLabel(hint.job).toLowerCase(),
    hint.outputChange,
  );
}

function focusables(root: HTMLElement): HTMLElement[] {
  return [
    ...root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
}

interface BodyProps {
  vm: ShortageVM;
  state: GameState;
  onConfirm: (answer: ShortageAnswer) => void;
  cfg: Config | undefined;
}

function ShortageBody({ vm, state, onConfirm, cfg }: BodyProps) {
  const [edits, setEdits] = useState<ShortageEdits>(noEdits);
  const [strip, setStrip] = useState<ShortageStrip | null>(null);
  const [stale, setStale] = useState(true);
  const [hints, setHints] = useState<Record<PersonId, ShortageHint> | null>(null);
  const [asking, setAsking] = useState(false);
  const [naming, setNaming] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const answer = useMemo(() => shortageAnswer(vm, edits), [vm, edits]);

  useEffect(() => {
    root.current?.focus();
  }, []);
  useEffect(() => {
    whenIdle(() => {
      setHints(shortageHints(state, cfg));
    });
  }, [state, cfg]);
  useEffect(() => {
    setStale(true);
    const timer = setTimeout(() => {
      whenIdle(() => {
        setStrip(shortageStrip(state, answer, cfg));
        setStale(false);
      });
    }, STRIP_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [state, answer, cfg]);
  useEffect(() => {
    if (asking) keep.current?.focus();
  }, [asking]);

  const edited = hasEdits(edits);
  const set = (id: PersonId, share: number): void => {
    const row = vm.rows.find((r) => r.id === id);
    const shares = { ...edits.shares };
    if (row !== undefined && Math.abs(row.proposed - share) < 1e-9 && !edits.pinned.includes(id))
      delete shares[id];
    else shares[id] = share;
    setEdits({ ...edits, shares });
  };
  const pin = (id: PersonId, on: boolean): void => {
    const pinned = on ? [...edits.pinned, id] : edits.pinned.filter((x) => x !== id);
    setEdits({ ...edits, pinned });
  };
  const discard = (): void => {
    setEdits(noEdits);
    setNaming(false);
    setAsking(false);
    root.current?.focus();
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (asking) {
        setAsking(false);
        root.current?.focus();
      } else if (edited) setAsking(true);
      return;
    }
    if (e.key === 'Tab' && root.current !== null) {
      const items = focusables(root.current);
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;
      if (
        e.shiftKey &&
        (document.activeElement === first || document.activeElement === root.current)
      ) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const resource = rationResourceLabel[vm.resource];
  const shown = (id: PersonId, proposed: number): number =>
    edits.shares[id] ?? strip?.allotted[id] ?? proposed;
  const nEdited = Object.keys(edits.shares).length;

  return (
    <div
      class="shortage"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortage-title"
      aria-describedby="shortage-summary"
      tabIndex={-1}
      ref={root}
      onKeyDown={onKeyDown}
    >
      <div class="shortage__inner">
        <header class="shortage__header">
          <h2 id="shortage-title">
            {shortageText.header(resource, vm.covered, vm.population, seasonLabel[vm.season])}
          </h2>
          <p id="shortage-summary">
            <span>
              {shortageText.shortfall(
                vm.shortfall.toFixed(1),
                vm.needed.toFixed(1),
                vm.available.toFixed(1),
              )}
            </span>{' '}
            {shortageText.policyNote(rationPolicyLabel[vm.policy], vm.floor)}
          </p>
          <p class="hint">{shortageText.noTimer}</p>
        </header>

        <ProjectionStrip strip={strip} stale={stale} />

        {vm.seedAvailable > 0 && (
          <section class="shortage__seed" aria-labelledby="shortage-seed-title">
            <h3 id="shortage-seed-title">{shortageText.seedHeading}</h3>
            <p class="hint">{shortageText.seedHint(vm.seedAvailable.toFixed(1))}</p>
            <label class="field">
              <span class="field__label">
                {shortageText.seedLabel}: <span class="num">{edits.eatSeed.toFixed(1)}</span>
              </span>
              <input
                type="range"
                min={0}
                max={vm.seedAvailable}
                step={Math.max(0.1, Math.round(vm.seedAvailable) / 20)}
                value={edits.eatSeed}
                onInput={(e) => {
                  setEdits({ ...edits, eatSeed: Number(e.currentTarget.value) });
                }}
              />
            </label>
          </section>
        )}

        <section aria-labelledby="shortage-list-title">
          <h3 id="shortage-list-title">{shortageText.listHeading}</h3>
          {nEdited > 0 && <p class="hint">{shortageText.edits(nEdited)}</p>}
          <table class="table shortage__table">
            <thead>
              <tr>
                <th scope="col">{shortageText.colName}</th>
                <th scope="col">{shortageText.colAge}</th>
                <th scope="col">{shortageText.colJob}</th>
                <th scope="col">{shortageText.colStrength}</th>
                <th scope="col">{shortageText.colShort}</th>
                <th scope="col">{shortageText.colHousehold}</th>
                <th scope="col">{shortageText.colShare}</th>
                <th scope="col">{shortageText.colSet}</th>
                <th scope="col">{shortageText.colPin}</th>
              </tr>
            </thead>
            <tbody>
              {vm.rows.map((row) => (
                <ShortageRow
                  key={row.id}
                  row={row}
                  share={shown(row.id, row.proposed)}
                  edited={edits.shares[row.id] !== undefined}
                  pinned={edits.pinned.includes(row.id)}
                  hint={hintText(hints?.[row.id])}
                  canPin={vm.resource !== 'warmth' || vm.season === 'winter'}
                  jobLabel={jobLabel}
                  onSet={set}
                  onPin={pin}
                />
              ))}
            </tbody>
          </table>
        </section>

        {naming && (
          <label class="field shortage__name">
            <span class="field__label">{shortageText.saveName}</span>
            <input
              type="text"
              maxLength={40}
              value={edits.saveAsOrder ?? ''}
              onInput={(e) => {
                setEdits({ ...edits, saveAsOrder: e.currentTarget.value });
              }}
            />
          </label>
        )}

        <footer class="shortage__actions">
          <button
            type="button"
            class="button button--primary"
            onClick={() => {
              onConfirm(answer);
            }}
          >
            {shortageText.confirm}
          </button>
          <button type="button" class="button" disabled={!edited} onClick={discard}>
            {shortageText.reset}
          </button>
          <button
            type="button"
            class="button"
            onClick={() => {
              setEdits({ ...edits, shares: equalShares(vm, edits.eatSeed) });
            }}
          >
            {shortageText.equal}
          </button>
          {naming ? (
            <button
              type="button"
              class="button"
              onClick={() => {
                setNaming(false);
                setEdits({ ...edits, saveAsOrder: null });
              }}
            >
              {shortageText.saveCancel}
            </button>
          ) : (
            <button
              type="button"
              class="button"
              onClick={() => {
                setNaming(true);
                setEdits({ ...edits, saveAsOrder: edits.saveAsOrder ?? shortageText.saveDefault });
              }}
            >
              {shortageText.saveOrder}
            </button>
          )}
          {edits.saveAsOrder !== null && edits.saveAsOrder.trim() !== '' && (
            <span class="hint">{shortageText.saving(edits.saveAsOrder.trim())}</span>
          )}
        </footer>

        {asking && (
          <div class="shortage__confirm" role="alertdialog" aria-labelledby="shortage-esc-title">
            <p id="shortage-esc-title">
              <strong>{shortageText.escTitle}</strong> {shortageText.escBody}
            </p>
            <button type="button" class="button" onClick={discard}>
              {shortageText.escDiscard}
            </button>
            <button
              type="button"
              class="button button--primary"
              ref={keep}
              onClick={() => {
                setAsking(false);
                root.current?.focus();
              }}
            >
              {shortageText.escKeep}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  /** The run's config when it is not the current one (a save's snapshot, ADR-0008). */
  cfg?: Config;
}

/** Mount once near the app root; renders only while the season is paused on a shortage. */
export function ShortageDialog({ cfg }: Props = {}) {
  const model = useComputed(() => (status.value === 'paused' ? shortageVM(game.value) : null));
  const vm = model.value;
  const state = game.value;
  if (vm === null || state === null) return null;
  return (
    <ShortageBody
      key={vm.promptId}
      vm={vm}
      state={state}
      cfg={cfg}
      onConfirm={(answer) => answerShortage(vm.promptId, answer)}
    />
  );
}
